import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SideDrawer, Skeleton, SubPartnerAccount, withStyles } from 'vopay';
import { Button } from 'UI';
import { useToast } from '@/providers/Toast.provider';
import useFetchPartnerPartnerDetails from '@/hooks/useApi/useFetchPartnerPartnerDetails';
import PartnerCard from '@/components/Partners/PartnerManagement/PartnerCard';
import toPartnerCredentials from '@/components/Partners/PartnerManagement/toPartnerCredentials.helper';
import PartnerPermissionsDrawer from '@/components/Partners/PartnerManagement/PartnerPermissionsDrawer/PartnerPermissionsDrawer';
import PartnerAccountInfo from './PartnerAccountInfo';
import styles from './PartnerDetailsDrawer.module.scss';

const cn = withStyles(styles);

type PartnerDetailsDrawerProps = {
  /** The list row the drawer was opened from; null keeps the drawer closed. */
  partner: SubPartnerAccount | null;
  /**
   * The caller is still fetching the row (the Onboarding Tracker has only the partner's ID): the drawer
   * opens on placeholders until `partner` arrives. A list passes its row at once and leaves this out.
   */
  isLoadingPartner?: boolean;
  onClose: () => void;
  onViewFullDetails: (partner: SubPartnerAccount) => void;
};

/**
 * The quick view of a partner account opened from its name in the list (spec 5.5): its identity
 * card with the API credentials strip and its Account Info, with the route to the full partner
 * page. The row already carries the name, status and dates; the credentials and program terms are
 * fetched on open.
 */
export default function PartnerDetailsDrawer({
  partner,
  isLoadingPartner = false,
  onClose,
  onViewFullDetails,
}: PartnerDetailsDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.PartnerDetailsDrawer' });
  const toast = useToast();
  const { partnerDetails, fetchingPartnerDetails, fetchPartnerPartnerDetails, reset } = useFetchPartnerPartnerDetails();
  const [hasDetailsFailed, setHasDetailsFailed] = useState(false);
  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);

  const partnerAccountID = partner?.PartnerAccountID;

  useEffect(() => {
    // The previous partner's details must not show under a newly opened partner's name, nor its
    // permissions drawer stay open over it.
    reset();
    setHasDetailsFailed(false);
    setIsPermissionsOpen(false);

    if (!partnerAccountID) return undefined;

    // The hook drops a superseded response, but its rejection still reaches this caller — so a
    // request the user has already moved on from must not mark the open partner as failed.
    let isCurrentPartner = true;

    fetchPartnerPartnerDetails(partnerAccountID).catch((error) => {
      if (!isCurrentPartner) return;

      // The hook has logged the cause. The card still renders what the row carries; the program
      // terms show as unavailable rather than loading forever.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      setHasDetailsFailed(true);

      toast.neutral(
        t('loadError', {
          defaultValue: 'Could not load the partner details, so some fields are unavailable: {{errorMessage}}',
          errorMessage,
        }),
      );
    });

    return () => {
      isCurrentPartner = false;
    };
    // toast is deliberately left out: the provider recreates its value every render, so depending
    // on it would refetch on every toast.
  }, [partnerAccountID, fetchPartnerPartnerDetails, reset, t]);

  // Undefined while loading, null when the request failed — the Account Info rows render each differently.
  const detailsOrFailure = partnerDetails ?? (hasDetailsFailed ? null : undefined);

  const credentials = toPartnerCredentials(partnerDetails);

  return (
    <SideDrawer
      isOpen={partner !== null || isLoadingPartner}
      onClose={onClose}
      title={t('title', { defaultValue: 'Partner Details' })}
      containerClassName={cn('drawer')}
      footer={
        <Button
          colorScheme="primary"
          size="large"
          className={cn('viewFullDetails')}
          disabled={!partner}
          onClick={() => partner && onViewFullDetails(partner)}
        >
          {t('viewFullDetails', { defaultValue: 'View Full Details' })}
        </Button>
      }
    >
      {!partner && isLoadingPartner && (
        <div className={cn('content')}>
          <Skeleton height="200px" borderRadius={16} />
          <Skeleton height="320px" borderRadius={16} />
        </div>
      )}

      {partner && (
        <>
          <div className={cn('content')}>
            <PartnerCard
              partner={partner}
              credentials={credentials}
              // Only an Active partner can have credentials to wait for.
              isLoadingCredentials={partner.IsActive && fetchingPartnerDetails}
              agreement={partnerDetails?.Agreement}
              onViewPermissionsClick={() => setIsPermissionsOpen(true)}
            />

            <PartnerAccountInfo partner={partner} details={detailsOrFailure} />
          </div>

          {/* A child of this drawer, so it stacks on it (SideDrawer's DrawerContext): this one recedes
              behind it rather than both drawing an overlay. It portals out, so the layout is unaffected.
              Keyed by the partner, so another partner gets a fresh drawer (no search carried over); it
              mounts closed, which is when SideDrawer can register its open with this drawer. */}
          <PartnerPermissionsDrawer
            key={partner.PartnerAccountID}
            partner={partner}
            isOpen={isPermissionsOpen}
            onClose={() => setIsPermissionsOpen(false)}
          />
        </>
      )}
    </SideDrawer>
  );
}
